import { useState, useMemo } from 'react';
import { useMatchRequests } from './hooks/useMatchRequests';
import type { MatchRecord, MatchStatusFilter } from './hooks/useMatchRequests';
import MatchTable from './components/MatchTable';
import MatchDetailModal from './components/MatchDetailModal';

export default function AdminMatchListPage() {
  const { data, loading, error, refetch } = useMatchRequests();
  const [statusFilter, setStatusFilter] = useState<MatchStatusFilter>('전체');
  const [search, setSearch] = useState('');
  const [selectedRow, setSelectedRow] = useState<MatchRecord | null>(null);

  const filtered = useMemo(() => {
    let rows = data;
    if (statusFilter !== '전체') {
      rows = rows.filter((r) => r.status === statusFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(
        (r) =>
          (r.match_number || '').toLowerCase().includes(q) ||
          r.requester_name.toLowerCase().includes(q) ||
          r.requester_phone.includes(q) ||
          r.receiver_name.toLowerCase().includes(q) ||
          r.receiver_phone.includes(q) ||
          (r.location || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [data, statusFilter, search]);

  const counts = useMemo(() => ({
    accepted: data.filter((r) => r.status === 'accepted').length,
    completed: data.filter((r) => r.status === 'completed').length,
    rejected: data.filter((r) => r.status === 'rejected').length,
    cancelled: data.filter((r) => r.status === 'cancelled').length,
  }), [data]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f7faf9' }}>
      {/* Header */}
      <div className="w-full border-b border-gray-100 bg-white sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#F57C00]">
              <i className="ri-hand-heart-line text-white text-sm"></i>
            </div>
            <div>
              <p className="text-xs text-gray-400">코리아케어러스 관리자</p>
              <h1 className="text-sm font-bold text-gray-900">매칭 내역 관리</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/admin/dashboard"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-gray-600 bg-gray-50 border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              <i className="ri-dashboard-line"></i>통계
            </a>
            <a
              href="/admin/care-list"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-gray-600 bg-gray-50 border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              <i className="ri-file-list-3-line"></i>신청목록
            </a>
            <a
              href="/admin/care-request"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#2E7D6B' }}
            >
              <i className="ri-add-line"></i>신규 접수
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {[
            { label: '전체 매칭', value: data.length, icon: 'ri-hand-heart-line', color: '#2E7D6B', bg: '#e8f5f1' },
            { label: '매칭완료', value: counts.accepted, icon: 'ri-check-double-line', color: '#059669', bg: '#d1fae5' },
            { label: '간병완료', value: counts.completed, icon: 'ri-verified-badge-line', color: '#2563eb', bg: '#dbeafe' },
            { label: '거절', value: counts.rejected, icon: 'ri-close-circle-line', color: '#dc2626', bg: '#fef2f2' },
            { label: '취소', value: counts.cancelled, icon: 'ri-forbid-2-line', color: '#6b7280', bg: '#f3f4f6' },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: s.bg }}>
                <i className={`${s.icon} text-lg`} style={{ color: s.color }}></i>
              </div>
              <div>
                <p className="text-xs text-gray-400">{s.label}</p>
                <p className="text-xl font-bold text-gray-900">{s.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          {/* 상태 필터 */}
          <div className="flex items-center gap-1.5 bg-white rounded-xl border border-gray-200 p-1">
            {[
              { key: '전체' as MatchStatusFilter, label: '전체' },
              { key: 'accepted' as MatchStatusFilter, label: '매칭완료' },
              { key: 'completed' as MatchStatusFilter, label: '간병완료' },
              { key: 'rejected' as MatchStatusFilter, label: '거절' },
              { key: 'cancelled' as MatchStatusFilter, label: '취소' },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                  statusFilter === f.key
                    ? 'bg-[#F57C00] text-white'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* 검색 */}
          <div className="relative flex-1 min-w-[200px]">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="이름, 연락처, 장소 검색"
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#F57C00] bg-white"
            />
          </div>

          {/* 결과 카운트 */}
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <span>전체 {data.length}건</span>
            {filtered.length !== data.length && (
              <span className="text-[#F57C00] font-bold">검색 {filtered.length}건</span>
            )}
          </div>

          {/* 새로고침 */}
          <button
            onClick={refetch}
            className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs text-gray-500 bg-white border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>새로고침
          </button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <i className="ri-loader-4-line text-3xl animate-spin mb-3" style={{ color: '#F57C00' }}></i>
            <p className="text-sm text-gray-400">데이터를 불러오는 중...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <i className="ri-error-warning-line text-3xl text-red-300 mb-3"></i>
            <p className="text-sm font-semibold text-red-400 mb-1">데이터 로딩 실패</p>
            <p className="text-xs text-gray-400 mb-4">{error}</p>
            <button
              onClick={refetch}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#F57C00' }}
            >
              다시 시도
            </button>
          </div>
        ) : (
          <MatchTable rows={filtered} onRowClick={setSelectedRow} />
        )}
      </div>

      {selectedRow && (
        <MatchDetailModal row={selectedRow} onClose={() => setSelectedRow(null)} />
      )}
    </div>
  );
}